import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "./UI";
import { ShieldCheck, CheckCircle2, Circle, CalendarClock, TrendingUp, ArrowRight } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");

export const VerificationDigestStrip = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/political-os/verification-digest").then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d?.entity_id || !d.summary) return null;
  const { score } = d.summary;
  const latest = (d.history || [])[0];
  return (
    <button
      onClick={() => navigate("/verify-campaign")}
      data-testid="verification-digest-strip"
      className="w-full text-left mb-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-emerald-400/40 transition-colors px-5 py-4 flex flex-wrap items-center gap-4 group"
    >
      <ShieldCheck className={`w-5 h-5 shrink-0 ${score >= 85 ? "text-emerald-300" : "text-amber-300"}`} />
      <div className="min-w-[130px]">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Verification</div>
        <div className="font-display text-base font-bold" data-testid="digest-strip-score">
          {score >= 85 ? "Fully verified" : `${score}% complete`}
        </div>
      </div>
      <div className="flex-1 min-w-[140px] h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full ${score >= 85 ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${score}%` }} />
      </div>
      <div className="text-[11px] text-slate-400" data-testid="digest-strip-momentum">
        {latest ? `Up from ${latest.from_score}% on ${fmtDate(latest.ts)}` :
          score >= 85 ? "Advertising & fundraising unlocked" : "Verified campaigns unlock everything"}
      </div>
      <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-300 transition-colors shrink-0" />
    </button>
  );
};

export const VerificationDigestCard = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/political-os/verification-digest").then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d?.entity_id || !d.summary) return null;
  const { score, components } = d.summary;
  return (
    <Card data-testid="verification-digest-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className={`w-5 h-5 ${score >= 85 ? "text-emerald-300" : "text-amber-300"}`} />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Verification Concierge</div>
            <h3 className="font-display text-lg font-bold" data-testid="digest-score">{score >= 85 ? "Fully verified" : `${score}% verified`}</h3>
          </div>
        </div>
        {score < 85 && (
          <button onClick={() => navigate("/verify-campaign")} className="text-xs text-cyan-300 hover:underline" data-testid="digest-verify-link">
            Finish verification →
          </button>
        )}
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-4" data-testid="digest-progress-bar">
        <div className={`h-full rounded-full transition-all ${score >= 85 ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${score}%` }} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        <div data-testid="digest-remaining">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Checklist</div>
          <ul className="space-y-1.5">
            {components.map(c => (
              <li key={c.key} className="flex items-center gap-2">
                {c.done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Circle className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
                <span className={c.done ? "text-slate-500 line-through" : "text-slate-300"}>{c.label}</span>
                {!c.done && <span className="ml-auto text-amber-300/80">+{c.points}</span>}
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="digest-history">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Momentum</div>
          {(d.history?.length || d.nudge_emails?.length) ? (
            <ul className="space-y-1.5">
              {(d.history || []).slice(0, 4).map((h, i) => (
                <li key={`h${i}`} className="text-slate-300" data-testid={`digest-history-${i}`}>
                  Score {h.from_score}% → <span className="text-emerald-300">{h.to_score}%</span>
                  <span className="text-slate-500 ml-1.5">{fmtDate(h.ts)}</span>
                </li>
              ))}
              {(d.nudge_emails || []).slice(0, 3).map((n, i) => (
                <li key={`n${i}`} className="text-slate-500" data-testid={`digest-nudge-${i}`}>
                  {n.email_type === "verification_progress" ? "Progress email sent to team" : "Checklist nudge sent"}
                  <span className="ml-1.5">{fmtDate(n.sent_at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-slate-600">No movement recorded yet — progress will appear here.</div>
          )}
        </div>
        <div data-testid="digest-schedule">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Upcoming nudges</div>
          <ul className="space-y-2">
            {(d.schedule || []).map((s, i) => (
              <li key={i} className="text-slate-300" data-testid={`digest-schedule-${i}`}>
                {s.label}
                <div className="text-slate-500">{s.cadence} · last ran {fmtDate(s.last_run)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};
